import Form from "./Form";
import classes from "../forms/forms.module.css";
import {ArchiveSelect, defaultArchiveSelectCreateValue} from "../archive/ArchiveSelects";
import React, {useCallback, useEffect, useState} from "react";
import {useArchiveContext} from "../../context/ArchiveContext";
import SubmitButton from "../buttons/SubmitButton";
import InputText from "../fields/InputText";
import InputTextArea from "../fields/InputTextArea";
import InputDate from "../fields/InputDate";
import {uploadArchiveFile, createArchiveByUrl} from "../../clients/ArchiveApiClient";
import InputFile from "../fields/InputFile";
import sendSuccessAlert, {sendErrorAlert} from "../../services/alertService";
import {MAX_VIDEO_BYTES, MAX_VIDEO_SECONDS, isVideoFile, getVideoDuration, formatSeconds} from "../../services/mediaService";

const CreateArchiveContentForm = () => {

    const [title, setTitle] = useState("");
    const [author, setAuthor] = useState("");
    const [category, setCategory] = useState(defaultArchiveSelectCreateValue);
    const [description, setDescription] = useState("");
    const [date, setDate] = useState(new Date());
    const {isOpenCreate, setIsOpenCreate, refresh} = useArchiveContext();
    const [loading, setLoading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [fileError, setFileError] = useState("");
    const [uploading, setUploading] = useState(false);
    const [uploadedUrl, setUploadedUrl] = useState("");
    const [uploadedContentType, setUploadedContentType] = useState(null);

    const close = useCallback(() => {
        setIsOpenCreate(false);
    }, [setIsOpenCreate]);

    useEffect(() => {
        if (!isOpenCreate) return;

        const handleKeyDown = (e) => {
            if (e.key === "Escape") {
                close()
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [isOpenCreate, close]);


    if (!isOpenCreate) return null;

    function resetFile(input) {
        if (input) input.value = "";
        setUploadedUrl("");
        setUploadedContentType(null);
    }

    async function handleFileChange(e) {
        const input = e.target;
        const f = input.files?.[0] || null;
        setFileError("");
        setUploadedUrl("");
        setUploadedContentType(null);
        if (!f) {
            return;
        }

        if (f.size > MAX_VIDEO_BYTES) {
            resetFile(input);
            setFileError("Файл слишком большой (более 1 ГБ).");
            sendErrorAlert("Файл слишком большой — загрузка отменена");
            return;
        }

        if (isVideoFile(f)) {
            try {
                const duration = await getVideoDuration(f);
                if (duration > MAX_VIDEO_SECONDS) {
                    resetFile(input);
                    setFileError(`Видео длиннее 15 минут (${formatSeconds(duration)}). Загрузите ролик не более 15:00.`);
                    sendErrorAlert("Видео длиннее 15 минут — загрузка отменена");
                    return;
                }
            } catch (err) {
                console.error("Не удалось прочитать длительность видео:", err);
                setFileError("Не удалось проверить длительность. Убедитесь, что видео не длиннее 15 минут.");
            }
        }

        // 1-я фаза: сразу заливаем файл — сабмит разблокируется после загрузки
        setUploading(true);
        setUploadProgress(0);
        try {
            const res = await uploadArchiveFile(f, (evt) => {
                if (evt.total) {
                    setUploadProgress(Math.round((evt.loaded * 100) / evt.total));
                }
            });
            setUploadedUrl(res.data.url);
            setUploadedContentType(res.data.content_type);
        } catch (err) {
            console.error("Ошибка загрузки файла:", err);
            setFileError("Не удалось загрузить файл. Попробуйте ещё раз.");
            sendErrorAlert("Не удалось загрузить файл");
            resetFile(input);
        } finally {
            setUploading(false);
        }
    }

    function disable() {
        return uploading ||
            !uploadedUrl ||
            title?.length === 0 ||
            category?.length === 0 ||
            description?.length === 0
    }

    function handleSubmit(e) {
        e.preventDefault();
        setLoading(true);
        createArchiveByUrl(uploadedUrl, uploadedContentType, title, author, description, category, date)
            .then(() => {
                close();
                sendSuccessAlert('Контент успешно добавлен');
                setUploadedUrl("");
                setUploadedContentType(null);
                setTitle("");
                setDescription("");
                setUploadProgress(0);
                refresh();
            })
            .catch((err) => {
                console.error('Error to create archive content', err);
                sendErrorAlert('Не удалось добавить контент. Попробуйте ещё раз.');
            })
            .finally(() => {
                setLoading(false);
            });
    }


    return (
        <div>
            <Form
                title={'Добавление контента в архив'}
                onSubmit={handleSubmit}
                onClose={close}
            >
                <InputFile
                    label={'Прикрепите фото или видео (видео — до 15 минут)'}
                    accept={"image/jpeg, image/jpg, image/png, video/mp4, video/webm, video/ogg"}
                    onChange={handleFileChange}
                />
                {fileError && (
                    <div style={{color: 'var(--danger)', fontSize: '13px', marginTop: '4px'}}>{fileError}</div>
                )}
                {uploading && (
                    <div style={{margin: '8px 0'}}>
                        <div style={{fontSize: '13px', marginBottom: '4px', color: 'var(--text-muted)'}}>
                            {uploadProgress < 100 ? `Загрузка файла: ${uploadProgress}%` : 'Обработка файла...'}
                        </div>
                        <div style={{background: 'var(--surface-2)', borderRadius: '4px', height: '6px', overflow: 'hidden'}}>
                            <div style={{
                                background: 'var(--accent)',
                                width: `${uploadProgress}%`,
                                height: '100%',
                                transition: 'width 0.3s ease'
                            }}/>
                        </div>
                    </div>
                )}
                {!uploading && uploadedUrl && (
                    <div style={{fontSize: '13px', marginTop: '4px', color: '#3aaf6b'}}>Файл загружен ✓</div>
                )}
                <InputText
                    text={'Заголовок'}
                    placeholder={'Укажите заголовок'}
                    value={title}
                    required={true}
                    onChange={(e) => setTitle(e.target.value)}
                />

                <div>
                    <label className={classes.label}>Выберите категорию <b className={classes.required}>*</b></label>
                    <ArchiveSelect value={category}
                                   create={true}
                                   handleChange={el => {
                                       setCategory(el);
                                   }}/>
                </div>

                <InputText
                    text={'Автор контента'}
                    placeholder={'Укажите автора'}
                    value={author}
                    onChange={(e) => setAuthor(e.target?.value) || 'Admin'}
                />

                <InputDate
                    text={'Дата создания'}
                    value={date}
                    onChange={(e) => setDate(e)}
                />

                <InputTextArea
                    text={'Описание'}
                    placeholder={'Опишите контент'}
                    value={description}
                    onChange={(e) => setDescription(e)}
                    required={true}
                />

                <SubmitButton
                    text={'Добавить контент'}
                    disabled={disable()}
                    isAnimating={loading}
                />
            </Form>
        </div>
    );
}

export default CreateArchiveContentForm;
